import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

/**
 * Records how an answer was produced: tab departures, pastes and typing rhythm.
 * A browser cannot see other tabs or another device, so this is evidence for a human
 * reviewer to weigh, never a verdict.
 */
export default function useIntegrityMonitor(token, active) {
  const events = useRef([]);
  const typing = useRef({});
  const leftAt = useRef(null);
  const [awayNotice, setAwayNotice] = useState(null);

  const push = useCallback((code, extra = {}) => {
    events.current.push({ code, at: new Date().toISOString(), ...extra });
  }, []);

  const flush = useCallback(async () => {
    if (!token) return;
    const batch = {
      events: events.current.splice(0, 100),
      typing: Object.values(typing.current),
    };
    if (!batch.events.length && !batch.typing.length) return;
    try {
      await api.post(`/assess/${token}/integrity`, batch);
    } catch {
      // Telemetry must never interrupt the assessment, but it must not lose events either:
      // put them back at the front of the queue for the next flush.
      events.current.unshift(...batch.events);
    }
  }, [token]);

  const row = (itemId) => {
    if (!typing.current[itemId]) {
      typing.current[itemId] = {
        item_id: itemId, keystrokes: 0, backspaces: 0, paste_count: 0, paste_chars: 0,
        final_chars: 0, first_keystroke_ms: null, active_ms: 0,
      };
    }
    return typing.current[itemId];
  };

  const lastTypedAt = useRef({});
  const startedAt = useRef(Date.now());

  const onKeyDown = useCallback((itemId, e) => {
    const r = row(itemId);
    if (r.first_keystroke_ms === null) r.first_keystroke_ms = Date.now() - startedAt.current;
    if (e.key === "Backspace" || e.key === "Delete") r.backspaces += 1;
    else if (e.key.length === 1) r.keystrokes += 1;
    const prev = lastTypedAt.current[itemId];
    const now = Date.now();
    // Count the gap between keystrokes as writing time, capped at 10s so idle time is excluded.
    // The first keystroke of a burst gets a nominal 400ms so short answers still get a rate.
    r.active_ms += prev && now - prev < 10_000 ? now - prev : 400;
    lastTypedAt.current[itemId] = now;
  }, []);

  const onPaste = useCallback((itemId, e) => {
    const text = e.clipboardData?.getData("text") || "";
    const r = row(itemId);
    r.paste_count += 1;
    r.paste_chars += text.length;
    push("paste", { item_id: itemId, chars: text.length });
  }, [push]);

  const onCopy = useCallback((itemId) => push("copy", { item_id: itemId }), [push]);

  const setLength = useCallback((itemId, length) => {
    row(itemId).final_chars = length;
  }, []);

  useEffect(() => {
    if (!active) return undefined;

    const onVisibility = () => {
      if (document.hidden) {
        leftAt.current = Date.now();
        push("tab_left");
      } else if (leftAt.current) {
        const awayMs = Date.now() - leftAt.current;
        leftAt.current = null;
        push("tab_return", { away_ms: awayMs });
        setAwayNotice({ seconds: Math.max(1, Math.round(awayMs / 1000)), at: Date.now() });
        flush();
      }
    };
    const onBlur = () => push("window_blur");
    const onFocus = () => push("window_focus");

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    const timer = setInterval(flush, 20_000);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
      clearInterval(timer);
    };
  }, [active, flush, push]);

  return {
    onKeyDown, onPaste, onCopy, setLength, flush,
    awayNotice, dismissAwayNotice: () => setAwayNotice(null),
  };
}
